import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { router } from '@/router';
import { ApiError } from '@/lib/api';
import { downloadVisitsCsv, getDashboard, listCompanies } from '@/lib/crm/api';
import { formatRelative } from '@/lib/crm/format';
import type { CrmCompany, CrmDashboardRow } from '@/lib/crm/types';

/** Бейдж «тенант / лид» — заведён ли клиент в систему (определяет осмотр). */
function TenantBadge({ linkedTenantId }: { linkedTenantId: string | null }) {
  return linkedTenantId ? (
    <Badge variant="primary" data-testid="badge-tenant">
      работаем в системе
    </Badge>
  ) : (
    <Badge variant="accent-brand" data-testid="badge-lead">
      не заведён в систему
    </Badge>
  );
}

// ----------------------- Клиенты (screen 1) -----------------------

function ClientsTab() {
  const [companies, setCompanies] = useState<CrmCompany[]>([]);
  const [counts, setCounts] = useState<Record<string, CrmDashboardRow>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string>('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // dashboard несёт activity_count + last_activity_at; companies — теги/реквизиты.
      const [cos, dash] = await Promise.all([listCompanies(), getDashboard()]);
      setCompanies(cos);
      const byId: Record<string, CrmDashboardRow> = {};
      for (const row of dash) byId[row.company_id] = row;
      setCounts(byId);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Не удалось загрузить клиентов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const c of companies) for (const t of c.tags) set.add(t);
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [companies]);

  // Сортировка по активности (DESC), затем по last_activity_at; count=0 в конце (AC-3.1/3.2).
  const sorted = useMemo(() => {
    const filtered = tagFilter
      ? companies.filter((c) => c.tags.includes(tagFilter))
      : companies;
    return [...filtered].sort((a, b) => {
      const ca = counts[a.id]?.activity_count ?? 0;
      const cb = counts[b.id]?.activity_count ?? 0;
      if (cb !== ca) return cb - ca;
      const la = counts[a.id]?.last_activity_at ?? '';
      const lb = counts[b.id]?.last_activity_at ?? '';
      return lb.localeCompare(la);
    });
  }, [companies, counts, tagFilter]);

  if (loading) {
    return (
      <div className="space-y-2" data-testid="clients-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {companies.length} клиентов · сортировка по активности
        </p>
        <div className="flex items-center gap-2">
          {allTags.length > 0 && (
            <select
              aria-label="Фильтр по тегу"
              data-testid="tag-filter"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            >
              <option value="">Фильтр: все теги</option>
              {allTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          )}
          <Button
            type="button"
            onClick={() => router.navigate({ to: '/crm/new' })}
            data-testid="new-client"
          >
            + Клиент
          </Button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground" data-testid="clients-empty">
            {tagFilter
              ? 'Нет клиентов с этим тегом.'
              : 'У вас ещё нет клиентов. Заведите первого через «+ Клиент».'}
          </p>
        </Card>
      ) : (
        <Card>
          <Table data-testid="clients-table">
            <TableHeader>
              <TableRow>
                <TableHead>Клиент</TableHead>
                <TableHead>Теги</TableHead>
                <TableHead>Активностей</TableHead>
                <TableHead>Последний контакт</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((c) => {
                const row = counts[c.id];
                const count = row?.activity_count ?? 0;
                return (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    data-testid={`client-row-${c.id}`}
                    onClick={() =>
                      router.navigate({
                        to: '/crm/$companyId',
                        params: { companyId: c.id },
                      })
                    }
                  >
                    <TableCell>
                      <div className="font-medium text-foreground">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.inn ? `ИНН ${c.inn}` : '—'}
                        {c.address ? ` · ${c.address}` : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {c.tags.length === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          c.tags.map((t) => (
                            <Badge key={t} variant="secondary">
                              {t}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold">{count}</span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatRelative(row?.last_activity_at)}
                    </TableCell>
                    <TableCell>
                      <TenantBadge linkedTenantId={c.linked_tenant_id} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      <p className="text-xs text-muted-foreground">
        Клиенты без активностей показываются в конце. Бейдж «тенант / лид» —
        заведён ли клиент в систему (определяет доступность осмотра с карты).
      </p>
    </div>
  );
}

// ----------------------- Дашборд (screen 5) -----------------------

function DashboardTab() {
  const [rows, setRows] = useState<CrmDashboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getDashboard();
        if (active) setRows(data);
      } catch (e) {
        if (active)
          setError(e instanceof ApiError ? e.detail : 'Не удалось загрузить дашборд');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const maxCount = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.activity_count), 0),
    [rows],
  );

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadVisitsCsv(from || undefined, to || undefined);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Не удалось выгрузить CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Клиенты по активности</h2>
          <span className="text-xs text-muted-foreground">
            по убыванию числа активностей
          </span>
        </div>

        {loading ? (
          <div className="space-y-2" data-testid="dashboard-loading">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="dashboard-empty">
            Пока нет данных по активности.
          </p>
        ) : (
          <div className="space-y-2" data-testid="dashboard-bars">
            {rows.map((r) => {
              const pct = maxCount > 0 ? (r.activity_count / maxCount) * 100 : 0;
              const isLead = !r.linked_tenant_id;
              return (
                <div
                  key={r.company_id}
                  className="grid grid-cols-[minmax(120px,1fr)_3fr_auto] items-center gap-3"
                >
                  <span className="truncate text-sm" title={r.name}>
                    {r.name}
                  </span>
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={
                        'h-full rounded-full ' +
                        (isLead ? 'bg-accent-brand' : 'bg-primary')
                      }
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                  <span className="w-6 text-right text-sm font-semibold">
                    {r.activity_count}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Амбер-бар = лид без тенанта. Клиенты с 0 активностей — в конце.
        </p>
      </Card>

      <Card className="p-5">
        <h2 className="text-sm font-semibold">Биллинг производителю СЗР</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Выгрузка визитов за период для отчёта/счёта. В CSV попадают только
          визиты с фактом выполнения (type=visit + дата выполнения),
          company-level.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="csv-from">Период с</Label>
            <Input
              id="csv-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="csv-to">по</Label>
            <Input
              id="csv-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-40"
            />
          </div>
          <Button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-csv"
          >
            <Download className="mr-1 h-4 w-4" />
            {exporting ? 'Выгружаем…' : 'Экспорт визитов CSV'}
          </Button>
        </div>
        {exportError && (
          <Alert variant="destructive" className="mt-3">
            <AlertDescription>{exportError}</AlertDescription>
          </Alert>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Колонки CSV: клиент, контакт, дата визита, ответственный, заметка.
          UTF-8 с BOM для Excel. Запланированные визиты (без даты выполнения) не
          включаются.
        </p>
      </Card>
    </div>
  );
}

/**
 * /crm — журнал клиентов агроконсультанта (AK-49).
 * Вкладка «Клиенты» (список, screen 1) + «Дашборд» (активность + CSV, screen 5).
 */
export function CrmListPage() {
  return (
    <AppShell>
      <div className="mx-auto w-full max-w-5xl space-y-6 p-6 pb-fab">
        <header>
          <h1 className="text-2xl font-semibold">Клиенты</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Журнал хозяйств-клиентов, контактов и активностей.
          </p>
        </header>

        <Tabs defaultValue="clients">
          <TabsList>
            <TabsTrigger value="clients" data-testid="tab-clients">
              Клиенты
            </TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              Дашборд
            </TabsTrigger>
          </TabsList>
          <TabsContent value="clients" className="mt-4">
            <ClientsTab />
          </TabsContent>
          <TabsContent value="dashboard" className="mt-4">
            <DashboardTab />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
